import { computed, effect, EffectRef, inject, Injectable, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { GlobalTemplateCollection } from '@models/types/dvm/GlobalTemplates.type';
import { Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TemplateData } from '@models/interfaces/dvm/Template.interface';
import { StyleTemplates } from '@models/types/dvm/ViewerStyles.type';
import { MapViewerStylesLevel } from '@3ddv/dvm-internal';
import { APP_CONFIG } from '@configuration/configuration';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { ViewerStylesOptions } from '@models/interfaces/dvm/ViewerStylesOptions.interface';

@Injectable({
  providedIn: 'root'
})
export class TemplateService {

  // API ENDPOINT
  private readonly endpoint:         string                              = `${environment.apiUrl}/api/global-templates/manage/`;


  // SERVICES
  private readonly config:           Signal<Configuration>               = inject(APP_CONFIG);
  private readonly http:             HttpClient                          = inject(HttpClient);


  // STATE
  private readonly templates:        Map<string, TemplateData>           = new Map<string, TemplateData>(); // Este se llena cada vez que la config cambia
  private readonly globalTemplates:  Map<number, TemplateData>           = new Map<number, TemplateData>(); // Esto se llena solo una vez
  private readonly currentTemplate:  WritableSignal<TemplateData | null> = signal<TemplateData | null>(null);
  public  readonly selectedTemplate: Signal<TemplateData|null>           = computed((): TemplateData | null => this.currentTemplate());


  // EFFECT
  private readonly onConfigChange:   EffectRef                           = effect((): void => {
    
    const configuration: ViewerStylesOptions = this.config().Dvm.viewer.styles;
    
    untracked((): void => {

      if(this.templates.size) this.clearTemplates();

      this.setTemplateMap();

      const selectedTemplate: TemplateData | undefined = this.templates.get(configuration.selected ?? '');

      if(selectedTemplate) this.setCurrentTemplate(selectedTemplate);

    });
    
  }, {allowSignalWrites: true});


  // GETTERS
  public get templateValues():       TemplateData[] {
    return [...this.templates.values()];
  }

  public get templateNames():        string[] {
    return [...this.templates.keys()].map(name => name.replace('[Global Template] ', ''));
  }
  
  // METHODS

  // #region Public Template Methods

  /**
   * Set Current Template
   * Establece la plantilla actual seleccionada.
   * Si la plantilla seleccionada ya es la actual, se deselecciona (establece en null).
   * Como puede venir de la api, si el mapa esta vacio y se setea una, se acepta, si no se comprueba que exista en el mapa.
   * @param templateId ID de la plantilla a establecer como actual.
   */
  public setCurrentTemplate(template: TemplateData): void {
    
    if(this.currentTemplate()?.name === template.name) return this.currentTemplate.set(null);

    if(this.templates.size === 0 || this.templates.has(template.name)) this.currentTemplate.set(template);

  }

  public removeTemplateByName(name: string): void {
    if(this.templates.has(name)) this.templates.delete(name);
  }

  /**
   * Get Template By Name
   * Obtiene una plantilla por su nombre.
   * @param name 
   * @returns 
   */
  public getTemplateByName(name: string): TemplateData | undefined {
    return this.templates.get(name);
  }

  /**
   * Get Global Templates
   * Obtiene las plantillas globales desde la API y actualiza el estado local.
   * Si el mapa de plantillas globales ya contiene datos, se limpia antes de la solicitud.
   * @returns Observable<GlobalTemplateCollection> Colección de plantillas globales.
   */
  public getGlobalTemplates(): Observable<GlobalTemplateCollection> {

    return this.http.get<GlobalTemplateCollection>(this.endpoint).pipe(
      tap((templates: GlobalTemplateCollection): void => this.setGlobalTemplateMap(templates))
    );

  }

  /**
   * Update Global Template
   * Actualiza una plantilla global existente en la API.
   * @param id ID de la plantilla a actualizar.
   * @param template Nuevo contenido de la plantilla.
   * @returns Observable<TemplateData> Plantilla actualizada.
   */
  public updateGlobalTemplate(id: number, template: MapViewerStylesLevel[]): Observable<TemplateData> {

    return this.http.put<TemplateData>(`${this.endpoint}${id}`, { template }).pipe(
      tap((updated: TemplateData): void => {
        if (this.globalTemplates.has(id)) {
          const existing: TemplateData = this.globalTemplates.get(id)!;
          this.globalTemplates.set(id, { ...existing, template: updated.template ?? template });
          this.setTemplateMap();
        }
      })
    );

  }

  public createGlobalTemplate(data: TemplateData): Observable<GlobalTemplateCollection> {

    return this.crateNewGlobalTemplate(data).pipe(
      switchMap(()=> this.getGlobalTemplates()),
      catchError((err) => { throw new Error(`Error creating global template: ${err.message}`) })
    );

  }

  public deleteGlobalTemplate(id: number): Observable<GlobalTemplateCollection> {
  
    return this.deleteGlobalTemplateById(id).pipe(
      switchMap(() => this.getGlobalTemplates()),
      catchError((err) => { throw new Error(`Error deleting global template: ${err.message}`) })
    );

  }

  // #endregion


  // #region Private Template Methods

  /**
   * Set Template Map
   * Este método formatea y establece el mapa de plantillas combinando las plantillas de estilo y las globales.
   */
  private setTemplateMap(): void {

    const styleTemplates:  TemplateData[] = this.formatStyleTemplates(this.config().Dvm.viewer.styles.templates ?? {}),
          globalTemplates: TemplateData[] = Array.from(this.globalTemplates.values()),
          templates:       TemplateData[] = [...styleTemplates, ...globalTemplates];

    for(const template of templates) this.templates.set(template.name, template);
    
  }
  
  /**
   * Clear Templates
   * Este método limpia el mapa de plantillas y resetea la plantilla actual.
   **/
  private clearTemplates(): void {
   
    this.templates.clear();

    this.currentTemplate.set(null);

  }

  /**
   * 
   * Set Global Template Map
   * Este método formatea y establece el mapa de plantillas globales. 
   * 
   * @param templates Colección de plantillas globales obtenida de la API.
   */
  private setGlobalTemplateMap(templates: GlobalTemplateCollection): void {
    
    if(this.globalTemplates.size) this.globalTemplates.clear();

    for(const template of templates.data) this.globalTemplates.set(template.id!, this.formatGlobalTemplate(template));
    
    this.setTemplateMap();

  }

  /**
   * Format Style Templates
   * Este método formatea las plantillas de estilo proporcionadas en un array de TemplateData.
   * @param templates Colección de plantillas de estilo a formatear.
   * @returns TemplateData[] Array de datos de plantillas formateadas.
   **/
  private formatStyleTemplates(templates: StyleTemplates): TemplateData[] {
    
    if(Object.entries(templates).length === 0) return [];

    return Array.from(Object.entries(templates).map(([key, value]: [string, MapViewerStylesLevel[]]) => {
      return {
        id:         null,
        name:       key,
        isGlobal:   false,
        template:   value
      }
    }));

  }

  /**
   * Format Template Data
   * Este método formatea los datos de una plantilla global.
   * @param template Datos de la plantilla a formatear.
   * @returns TemplateData Datos formateados de la plantilla.
   */
  private formatGlobalTemplate(template: TemplateData): TemplateData {

    // Añadimos que es Global
    template['isGlobal'] = true;

    // Formalizamos nombre
    template['name']  = '[Global Template] ' + template.name;

    // Aseguramos que el template es un array
    if(!Array.isArray(template.template))
    template['template'] = [template.template];
    
    // ... ( Lo que venga )

    return template

  }

  private deleteGlobalTemplateById(id: number): Observable<void> {
    
    return this.http.delete<void>(`${this.endpoint}${id}`)

  }

  private crateNewGlobalTemplate(data: TemplateData): Observable<TemplateData> {

    return this.http.post<TemplateData>(this.endpoint, data)

  }
  // #endregion
    

}
